import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getQueryFn } from "@/lib/queryClient";
import { setAnalyticsUserId, clearAnalyticsUserId } from "@/lib/analytics";
import type { User } from "@shared/schema";

export function useAuth() {
  const { data: user, isLoading, error, refetch } = useQuery<User | null>({
    queryKey: ["/api/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 min — ricontrolla sessione regolarmente
    refetchOnWindowFocus: true, // ricontrolla quando l'app torna in foreground (mobile)
  });

  // GA4 User-ID. Agganciato qui e non ai form di login perche' questo hook
  // conosce l'utente a ogni caricamento, sessione gia' attiva inclusa: chi
  // torna il giorno dopo non ripassa dal login, e senza questo GA lo conterebbe
  // come nuovo browser anonimo. setUserId/clearUserId sono no-op finche' manca
  // il consenso cookie: l'id resta in attesa e viene applicato se GA parte.
  // Con l'id passa la fonte d'iscrizione, che GA4 riceve come proprieta' utente.
  useEffect(() => {
    if (isLoading) return; // sessione ancora sconosciuta: non e' un logout
    if (user?.id) {
      setAnalyticsUserId(String(user.id), user.signupSource);
    } else {
      clearAnalyticsUserId();
    }
  }, [user?.id, user?.signupSource, isLoading]);

  return {
    user,
    isLoading,
    error,
    refetch,
    isAuthenticated: !!user,
  };
}
